// Component: PriceSparkline.jsx
// Purpose: Tiny inline-SVG "price over the last 30 minutes" line; profit/loss coloured by net direction.
//          Points are {t,v} (unix s, price) placed on a TIME axis whose right edge is the newest
//          point and whose width is WINDOW_SEC; bare numbers are still accepted (index axis).

import React from 'react';

const W = 88;
const H = 22;
const PAD = 2;
//--> One authority for the window; usePriceSparkline re-exports it for its fetch/slice.
export const WINDOW_SEC = 30 * 60;

/**
 * @param {{ points: ({t:number,v:number}|number)[], className?: string }} props
 */
const PriceSparkline = ({ points, className = '' }) => {
  const raw = (points || []).map((p, i) => (
    typeof p === 'number' ? { t: i, v: p, indexAxis: true } : { t: Number(p?.t), v: Number(p?.v) }
  )).filter((p) => Number.isFinite(p.v) && Number.isFinite(p.t));
  if (raw.length < 2) return null;
  const timed = raw.every((p) => !p.indexAxis);

  const first = raw[0].v;
  const last = raw[raw.length - 1].v;
  const up = last >= first;
  const vals = raw.map((p) => p.v);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  //--> time axis: newest point on the right edge, WINDOW_SEC wide; points older than the window
  //    clamp to the left edge (the hook already trims them)
  const tEnd = raw[raw.length - 1].t;
  const tStart = timed ? tEnd - WINDOW_SEC : raw[0].t;
  const tSpan = tEnd - tStart || 1;
  const x = (p) => Math.max(0, Math.min(1, (p.t - tStart) / tSpan)) * W;
  const y = (v) => PAD + (1 - (v - min) / span) * (H - 2 * PAD);
  const poly = raw.map((p) => `${x(p).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ');
  const pct = first ? ((last - first) / first) * 100 : 0;
  const label = `Price over the last 30 minutes: ${up ? 'up' : 'down'} ${Math.abs(pct).toFixed(1)}%`;

  return (
    <svg
      role="img"
      aria-label={label}
      data-testid="price-sparkline"
      data-direction={up ? 'up' : 'down'}
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      className={`inline-block align-middle ${up ? 'text-profit' : 'text-loss'} ${className}`}
    >
      <line x1="0" x2={W} y1={y(first)} y2={y(first)} stroke="currentColor" strokeOpacity="0.25" strokeDasharray="2 2" />
      <polyline data-testid="price-sparkline-line" points={poly} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

export default PriceSparkline;
